import type { HTMLAttributes, ReactNode } from 'react';

import classNames from 'classnames';

import styles from './GridItem.module.css';

import { getStyles as getFlexBoxStyles } from '../Flex/getStyles';
import type { FlexResponsiveProps } from '../Flex/types';
import { type GridGrowProps, getGrowStyles } from './grow';

export type GridItemSpan = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export interface GridItemProps
  extends FlexResponsiveProps, GridGrowProps, HTMLAttributes<HTMLElement> {
  /** Set the span of the item for `xs` viewport. */
  xs?: GridItemSpan;
  /** Set the span of the item for `sm` viewport. */
  sm?: GridItemSpan;
  /** Set the span of the item for `md` viewport. */
  md?: GridItemSpan;
  /** Set the span of the item for `lg` viewport. */
  lg?: GridItemSpan;
  /** Set the span of the item for `lg` viewport. */
  xl?: GridItemSpan;
  className?: string;
  children?: ReactNode;

  /** @deprecated Use `justify` instead */
  justifyContent?: FlexResponsiveProps['justify'];
  /** @deprecated Use `direction` instead */
  flexDirection?: FlexResponsiveProps['direction'];
}

/**
 * Use `GridItem` as child of `Grid` to define the span of its content across
 * different viewports.
 *
 * @see https://corgi-x.tfd.engineering/components/grid
 */
export function GridItem(props: GridItemProps) {
  const {
    xs,
    sm,
    md,
    lg,
    xl,
    className,

    justify,
    direction,
    justifyContent,
    flexDirection,
    alignItems,
    alignContent,
    wrap,

    growContent,
    children,
    ...divAttributes
  } = props;

  // Set the default width to 12 columns, until the first given viewport is met
  const defaultWidthStyles = {
    [styles['col-xs-12']]: !xs,
    [styles['col-sm-12']]: !xs && !sm,
    [styles['col-md-12']]: !xs && !sm && !md,
    [styles['col-lg-12']]: !xs && !sm && !md && !lg,
    [styles['col-xl-12']]: !xs && !sm && !md && !lg && !xl,
  };

  const flexboxStyles = getFlexBoxStyles({
    justify: justifyContent ?? justify,
    direction: flexDirection ?? direction,
    alignItems,
    alignContent,
    wrap,
  });

  const growStyles = getGrowStyles({ growContent });

  const flexStyle = {
    [styles.flex]: flexboxStyles.length > 0 || growStyles.length > 0,
  };

  const widthStyles = [];
  if (xs) {
    widthStyles.push(styles[`col-xs-${xs}`]);
  }
  if (sm) {
    widthStyles.push(styles[`col-sm-${sm}`]);
  }
  if (md) {
    widthStyles.push(styles[`col-md-${md}`]);
  }
  if (lg) {
    widthStyles.push(styles[`col-lg-${lg}`]);
  }
  if (xl) {
    widthStyles.push(styles[`col-xl-${xl}`]);
  }

  const cssClass = classNames(
    styles.root,
    defaultWidthStyles,
    widthStyles,
    flexStyle,
    flexboxStyles,
    growStyles,
    className
  );

  return (
    <div {...divAttributes} className={cssClass}>
      {children}
    </div>
  );
}
